import React, { useContext, useMemo, useState, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { GlobalContext } from "../../Context/GlobalContext";
import { Toaster } from "react-hot-toast";
import {
  Building2,
  Search,
  BarChart2,
  FileText,
  CheckCircle2,
  ReceiptText,
  UserCheck,
  UserX,
  IndianRupee,
} from "lucide-react";
import { Dialog, Transition } from "@headlessui/react";
import { twoDecimalPlaces } from "../../Utils/utils";

/* --- Equal-width icon pill --- */
const Pill = ({ color = "slate", icon: Icon = CheckCircle2, children }) => {
  const map = {
    green: "bg-emerald-50 text-emerald-700 border-emerald-200",
    red: "bg-rose-50 text-rose-700 border-rose-200",
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    yellow: "bg-amber-50 text-amber-700 border-amber-200",
    violet: "bg-violet-50 text-violet-700 border-violet-200",
    slate: "bg-slate-50 text-slate-700 border-slate-200",
  };
  return (
    <span
      className={`inline-flex h-6 w-28 items-center justify-center gap-1.5 rounded-full border px-2 text-[11px] font-semibold ${map[color]}`}
    >
      <Icon size={14} /> {children}
    </span>
  );
};

function Confirm({
  open,
  title,
  desc,
  confirmText = "Confirm",
  onClose,
  onConfirm,
}) {
  return (
    <Transition show={open} as={Fragment}>
      <Dialog onClose={onClose} className="relative z-50">
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-150"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" />
        </Transition.Child>
        <div className="fixed inset-0 grid place-items-center p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-150"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-100"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
              <Dialog.Title className="text-base font-semibold">
                {title}
              </Dialog.Title>
              {desc ? (
                <p className="mt-2 text-sm text-gray-600">{desc}</p>
              ) : null}
              <div className="mt-4 flex justify-end gap-2">
                <button
                  className="h-9 rounded-xl border px-3 text-sm"
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button
                  className="h-9 rounded-xl bg-rose-600 px-4 text-sm font-medium text-white hover:bg-rose-700"
                  onClick={onConfirm}
                >
                  {confirmText}
                </button>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}


export default function ReconSettlement() {
  const navigate = useNavigate();
  const { AllClients = [] } = useContext(GlobalContext);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(""); // "1" active, "0" inactive
  const [dateFilter, setDateFilter] = useState("");
  const [pendingRoute, setPendingRoute] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const filtered = useMemo(() => {
    return AllClients.filter((c) => {
      const org = (c.organization_name || "").toLowerCase();
      const matchSearch = org.includes(search.toLowerCase());
      const matchStatus = statusFilter ? String(c.us_status) === statusFilter : true;
      const matchDate = dateFilter
        ? String(c.us_last_login || "").includes(dateFilter)
        : true;
      return matchSearch && matchStatus && matchDate;
    });
  }, [AllClients, search, statusFilter, dateFilter]);

  const onAction = (type, client) => {
    const orgName = encodeURIComponent(
      client.organization_name || client.us_name || ""
    );

    let path = "";
    let label = "";

    if (type === "report") {
      path = `/admin/recon/settlement/Report/${client.us_code}/${orgName}`;
      label = "Payment Report";
    } else if (type === "settlement") {
      // New Payout route
      path = `/admin/recon/settlement-report/${client.us_code}/${orgName}`;
      label = "settlement report";
    } else if (type === "restaurantWise") {
      path = `/admin/recon/restaurant-wise/${client.us_code}/${orgName}`;
      label = "Restaurant Wise Transactions";
    }

    setPendingRoute({ path, label });
    setConfirmOpen(true);
  };

  const proceed = () => {
    if (pendingRoute) {
      navigate(pendingRoute.path);
      setPendingRoute(null);
      setConfirmOpen(false);
    }
  };

  return (
    <div className="p-3 lg:p-5">
      <Toaster position="top-right" />

      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-[#01C37D]/10 text-[#01C37D]">
            <Building2 size={18} />
          </div>
          <div>
            <h1 className="text-xl font-semibold">Recon Settlements</h1>
            <p className="text-sm text-gray-500">
              Choose a client to view settlement reports, analytics & payouts
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="rounded-2xl border bg-white p-3">
        <div className="grid gap-3 md:grid-cols-3">
          <div className="flex items-center gap-2 rounded-xl border px-3">
            <Search size={16} className="text-gray-500" />
            <input
              placeholder="Search by Organization…"
              className="h-10 w-full text-sm outline-none"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select
            className="h-10 w-full rounded-xl border bg-white px-3 text-sm outline-none"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Status</option>
            <option value="1">Active</option>
            <option value="0">Inactive</option>
          </select>

          <input
            type="date"
            className="h-10 w-full rounded-xl border px-3 text-sm outline-none"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
          />
        </div>
      </div>

      {/* Table */}
      <div className="mt-3 overflow-hidden rounded-2xl border bg-white">
        <div className="max-h-[70vh] overflow-auto">
          <table className="min-w-full table-fixed text-left text-sm">
            <colgroup>
              <col className="w-[22%]" />
              <col className="w-[14%]" />
              <col className="w-[12%]" />
              <col className="w-[14%]" />
              <col className="w-[14%]" />
              <col className="w-[15%]" />
              
            </colgroup>
            <thead className="sticky top-0 z-10 bg-[#01C37D] text-white">
              <tr>
                {[
                  "Organization",
                  "User",
                  "Contact",
                  "Wallet",
                  "Status",
                  "Status/Action",
                ].map((h) => (
                  <th key={h} className="px-3 py-2 font-semibold">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => {
                const isActive = String(c.us_status) === "1";
                return (
                  <tr key={c.us_code} className="border-t">
                    {/* Organization */}
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-between gap-2">
                        <span>{c.organization_name || "N/A"}</span>
                      </div>
                    </td>

                    {/* Status */}
                    

                    {/* User Code & Name */}
                    <td className="px-3 py-2">
                      <div className="flex flex-col">
                        <span>{c.us_code || "N/A"}</span>
                        <span className="text-sm text-gray-500">
                          {c.us_name || "N/A"}
                        </span>
                      </div>
                    </td>

                    {/* Email & Contact */}
                    <td className="px-3 py-2">
                      <div className="flex flex-col">
                        <span>{c.us_email || "N/A"}</span>
                        <span className="text-sm text-gray-500">
                          {c.us_contact || c.us_loginid || "N/A"}
                        </span>
                      </div>
                    </td>

                    {/* Balance */}
                    <td className="px-3 py-2">
                      ₹ {twoDecimalPlaces(c.balance) ?? "—"}
                    </td>
                    <td className="px-3 py-2">
                      <Pill
                        color={isActive ? "green" : "red"}
                        icon={isActive ? UserCheck : UserX}
                      >
                        {isActive ? "Active" : "Inactive"}
                      </Pill>
                    </td>

                    {/* Actions */}
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-2">
                        <button
                          className="inline-flex items-center gap-1 rounded-xl border px-2.5 py-1.5 text-xs hover:bg-gray-50 transition"
                          onClick={() => onAction("settlement", c)}
                          title="Settlement Analytics"
                        >
                          <BarChart2 size={14} />
                          Settlement
                        </button>

                        <button
                          className="inline-flex items-center gap-1 rounded-xl border px-2.5 py-1.5 text-xs hover:bg-gray-50 transition"
                          onClick={() => onAction("restaurantWise", c)}
                          title="Restaurant Wise Transactions"
                        >
                          <ReceiptText size={14} />
                          Rewise
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    className="px-4 py-6 text-center text-gray-500"
                  >
                    No clients match your filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Confirm navigation */}
      <Confirm
        open={confirmOpen}
        title={`Open ${pendingRoute?.label}?`}
        desc="This may load a large dataset. You can change dates and filter inside the next screen."
        confirmText="Open"
        onClose={() => setConfirmOpen(false)}
        onConfirm={proceed}
      />
    </div>
  );
}
